import { CircleLoader } from "@/components/shared/loaders";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { useTranslation } from "react-i18next";
import { useEffect, useMemo } from "react";
import { FieldErrors, Resolver, SubmitHandler, useForm, useFieldArray } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Form } from "@/components/ui/form";
import { AdvancedCostParametersInput, FetchAllOrganizationsDocument, FetchAllOrganizationsQuery, FetchAllOrganizationsQueryVariables } from "@/graphql/__generated__/graphql";
import useCreateOrganizationsMutationHook from "@/hooks/mutations/useCreateOrganizationsMutation";
import { convertDecimalToBp } from "@/utils/helpers";
import { PlusIcon, ArrowLeftIcon } from "lucide-react";
import { ChildOrganizationForm } from "./Components/ChildOrganizationForm";
import BaseCreateFormComponent from "./Components/BaseCreateFormComponent";
import { createFormSchema, defaultFormValues, TCreateOrganizationFormSchema } from "./CreateOrganiationsModel";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@apollo/client/react";

/**
 * CreateOrganizationsPage component for creating new organizations
 * Displays a form with base organization fields, advanced cost parameters, and child organizations
 * @returns JSX element
 */
function CreateOrganizationsPage() {
  const { t } = useTranslation(["modals", "translation"]);
  const navigate = useNavigate();

  const { data: allOrganizationsData, loading: allOrganizationsLoading } = useQuery<FetchAllOrganizationsQuery, FetchAllOrganizationsQueryVariables>(FetchAllOrganizationsDocument,{
    fetchPolicy: "network-only",
  });

  const { createOrganizationsMutation, loading: createOrganizationsLoading } = useCreateOrganizationsMutationHook();

  const parentOrganizationOptions = useMemo(() => {
    return allOrganizationsData?.fetchAllOrganizations.map((organization) => ({
      label: organization.name,
      value: organization.id
    })) ?? [];
  }, [allOrganizationsData]);

  const formSchema = createFormSchema(t);

  const form = useForm<TCreateOrganizationFormSchema>({
    resolver: zodResolver(formSchema) as Resolver<TCreateOrganizationFormSchema>,
    mode: "onChange",
    defaultValues: defaultFormValues,
  });

  const { fields: childrenFields, append: appendChild, remove: removeChild, replace: replaceChildren } = useFieldArray({
    control: form.control,
    name: "input.children"
  });

  const isMainOrganization = form.watch("isMainOrganization");

  // Reset form when component mounts
  useEffect(() => {
    form.reset(defaultFormValues);
    replaceChildren([]);
  }, [form, replaceChildren]);

  // Handle isMainOrganization change
  useEffect(() => {
    if (isMainOrganization) {
      // If main organization, clear parentId
      form.setValue("input.parentId", null);
      // Clear any parentId validation errors
      form.clearErrors("input.parentId");
    } else {
      // If not main organization, clear advanced cost parameters
      form.setValue("advancedCostParameters", defaultFormValues.advancedCostParameters);
      // Clear any advanced cost parameters validation errors
      form.clearErrors("advancedCostParameters");
    }
  }, [isMainOrganization, form]);

  const handleAddChild = () => {
    appendChild({
      organization: {
        name: "",
        province: "",
        shortName: ""
      },
      parentId: null,
      children: undefined
    });
  };

  const handleRemoveChild = (index: number) => {
    removeChild(index);
  };

  const onSubmit: SubmitHandler<TCreateOrganizationFormSchema> = (data) => {
    const { isMainOrganization } = data;

    createOrganizationsMutation({
      variables: {
        input: data.input,
        ...(isMainOrganization && {
          advancedCostParameters: {
            ...data.advancedCostParameters,
            employeeFluctuation: data.advancedCostParameters?.employeeFluctuation
              ? convertDecimalToBp(data.advancedCostParameters.employeeFluctuation)
              : 0
          } as AdvancedCostParametersInput
        }),
      }
    }).then(() => {
      // Navigate back to organizations list on successful creation
      navigate("/admin/organizations");
    });
  };

  const onInvalid = (errors: FieldErrors<TCreateOrganizationFormSchema>) => console.error(errors);

  const handleBack = () => {
    navigate("/admin/organizations");
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit, onInvalid)} className="flex flex-col h-full">
        {/* Header */}
        <div className="flex flex-col px-4 py-3">
          <div className="flex items-center justify-between">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleBack}
              className="gap-2"
              type="button"
            >
              <ArrowLeftIcon className="h-4 w-4" />
              {t("common:buttons.back")}
            </Button>
            <Button variant="default" type="submit" disabled={createOrganizationsLoading} className="gap-2">
              <CircleLoader visible={createOrganizationsLoading} height={16} width={16} strokeWidth={2} />
              {t("common:buttons.create")}
            </Button>
          </div>
          <Separator className="mt-2" />
        </div>

        {/* Scrollable content */}
        <div className="flex-1 overflow-y-auto">
          <div className="px-4 space-y-4">
            {/* Page Title and Description */}
            <div className="flex flex-col">
              <h1 className="text-lg font-semibold">{t("createorganization.title")}</h1>
              <p className="text-sm text-muted-foreground">{t("createorganization.description")}</p>
            </div>

            {/* Base Organization Form */}
            <BaseCreateFormComponent
              form={form}
              parentOrganizationOptions={parentOrganizationOptions}
              allOrganizationsLoading={allOrganizationsLoading}
            />

            {/* Children Organizations Section */}
            <Separator />
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-medium">{t("createorganization.childrenorganizations")}</h2>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={handleAddChild}
                >
                  <PlusIcon className="h-4 w-4 mr-1" />
                  {t("createorganization.newchildorganization")}
                </Button>
              </div>

              <div className="space-y-4 pb-[400px]">
                {childrenFields.map((_, index) => (
                  <ChildOrganizationForm
                    key={`input.children.${index}`}
                    form={form}
                    fieldName={`input.children.${index}`}
                    level={0}
                    onRemove={() => handleRemoveChild(index)}
                    canRemove={true}
                  />
                ))}
                {childrenFields.length === 0 && (
                  <div className="flex flex-col gap-2 text-center py-8 text-muted-foreground">
                    <p>{t("createorganization.nochildorganizationsaddedyet")}</p>
                    <p className="text-sm">{t("createorganization.clicknewchildorganizationtoaddone")}</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </form>
    </Form>
  );
}

export default CreateOrganizationsPage;
